import { LanguageManagerMessages, tableHeaderMessages } from "./messages";
import React, { useCallback, useMemo, useState } from "react";
import { getAllEntities, getEntity, updateEntity } from "./infrastructure/languages.api";
import { useProperty } from "../../components/property-provider";
import SimpleCrud, { defaultCrudMessages } from "../shared/crud/simple-crud";
import { FormConfiguration } from "../shared/crud/form";
import { SwitchGroup } from "../shared/widgets/SwitchGroup";
import { LanguageEntity } from "./domain/languages.entity";
import originalSchema from "../../gen/schema/witbooking.backoffice.languages.v1/Language.json";
import { TableColumns } from "../shared/crud/list";
import { StringRenderer } from "../shared/renderers/table";
import { withAuthorizationVerifier } from "../../components/authorization-verifier";
import { SECTION } from "../../lib/constants";
import { RendererProps } from "../shared/renderers";
import { ErrorSchema, IChangeEvent } from "react-jsonschema-form";
import { useIntl } from "react-intl";

const messages = {
  ...defaultCrudMessages,
  ...LanguageManagerMessages,
  tableHeaders: tableHeaderMessages,
};

const schema = {
  ...originalSchema,
};

function CustomBooleanRenderer({ value: valueReq = "" }: RendererProps<string | boolean>) {
  return <>{valueReq ? "✔" : ""}</>;
}

function buildFormConfiguration(): FormConfiguration<LanguageEntity> {
  return {
    __fieldOrder: ["id", "name", "code", "primary", "active", "visible", "*"],
    __useProtoStyleArrays: true,
    id: {
      hidden: true,
    },
    name: {
      messages: {
        label: messages.tableHeaders.name,
      },
      readonly: true,
    },
    code: {
      messages: {
        label: messages.tableHeaders.code,
      },
      readonly: true,
    },
    primary: {
      widget: SwitchGroup,
      messages: {
        label: messages.tableHeaders.primary,
        description: messages.defaultDescription,
      },
    },
    active: {
      widget: SwitchGroup,
      messages: {
        label: messages.tableHeaders.active,
      },
    },
    visible: {
      widget: SwitchGroup,
      messages: {
        label: messages.tableHeaders.visible,
      },
    },
  };
}

function buildTableColumns(): TableColumns<LanguageEntity> {
  return {
    name: {
      renderer: StringRenderer,
      width: 150,
    },
    code: {
      renderer: StringRenderer,
      width: 150,
    },
    active: {
      renderer: CustomBooleanRenderer,
      width: 150,
    },
    visible: {
      renderer: CustomBooleanRenderer,
      width: 150,
    },
    primary: {
      renderer: CustomBooleanRenderer,
      width: 150,
    },
  };
}

function LanguageManager() {
  const intl = useIntl();
  const propertyInfo = useProperty();
  const [visibleLanguages, setVisibleLanguages] = useState(0);

  const onGetAll = useCallback(async () => {
    const languages = await getAllEntities(propertyInfo.token);
    setVisibleLanguages(languages.filter((l) => l.visible).length);
    return languages;
  }, [
    propertyInfo.token,
  ]);
  const onGetOne = useCallback(async (id: string) => getEntity(id, propertyInfo.token), [
    propertyInfo.token,
  ]);
  const onUpdate: (entity: LanguageEntity) => Promise<void> = useCallback(
    async entity => {
      if (visibleLanguages <= 1 && !entity.visible) {
        throw new Error(`- ${intl.formatMessage(messages.errorLastVisibleLanguage)}`);
      }
      return await updateEntity(entity, propertyInfo.token)
    },
    [propertyInfo.token, visibleLanguages]
  );

  const formConfiguration = useMemo(() => buildFormConfiguration(), []);
  const tableColumns = useMemo(() => buildTableColumns(), []);

  return (
    <SimpleCrud
      formConfiguration={formConfiguration}
      schema={schema}
      tableColumns={tableColumns}
      messages={messages}
      onGetAll={onGetAll}
      onGetOne={onGetOne}
      onUpdate={onUpdate}
      hideExportButton
      section={SECTION.LANGUAGE_SETTINGS}
    />
  );
}

export default withAuthorizationVerifier(LanguageManager);
